import React from 'react';
import {
  Box,
  Typography,
  Button,
  Link,
  IconButton,
} from '@mui/material';
import { motion } from 'framer-motion';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

const Footer = () => {
  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#041D5C',
        color: '#ffffff',
        py: { xs: 6, md: 8 },
        px: { xs: 2, md: 4 },
      }}
    >
      <Box
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
        }}
      >
        {/* Main Footer Content */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '2fr 1fr 1fr 2fr',
              md: '2fr 1fr 1fr 2fr',
            },
            gap: { xs: 4, md: 6 },
            mb: { xs: 4, md: 6 },
          }}
        >
          {/* SkyShift Brand Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', md: '1.75rem' },
                  mb: 2,
                  color: '#ffffff',
                }}
              >
                SkyShift
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#e2e8f0',
                  fontSize: '0.95rem',
                  lineHeight: 1.6,
                  maxWidth: '280px',
                }}
              >
                SkyShift is a modern SaaS-based ETL platform that helps
                businesses simplify how they move and manage data.
              </Typography>
            </Box>
          </motion.div>

          {/* Product Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  mb: 2,
                  color: '#ffffff',
                }}
              >
                Product
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Link
                  href="#"
                  sx={{
                    color: '#e2e8f0',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    '&:hover': {
                      color: '#ffffff',
                      textDecoration: 'none',
                    },
                  }}
                >
                  Overview
                </Link>
                <Link
                  href="#"
                  sx={{
                    color: '#e2e8f0',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    '&:hover': {
                      color: '#ffffff',
                      textDecoration: 'none',
                    },
                  }}
                >
                  Features
                </Link>
                <Link
                  href="#"
                  sx={{
                    color: '#e2e8f0',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    '&:hover': {
                      color: '#ffffff',
                      textDecoration: 'none',
                    },
                  }}
                >
                  Pricing
                </Link>
              </Box>
            </Box>
          </motion.div>

          {/* Resources Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  mb: 2,
                  color: '#ffffff',
                }}
              >
                Resources
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Link
                  href="#"
                  sx={{
                    color: '#e2e8f0',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    '&:hover': {
                      color: '#ffffff',
                      textDecoration: 'none',
                    },
                  }}
                >
                  Documentation
                </Link>
                <Link
                  href="#"
                  sx={{
                    color: '#e2e8f0',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    '&:hover': {
                      color: '#ffffff',
                      textDecoration: 'none',
                    },
                  }}
                >
                  Help center
                </Link>
              </Box>
            </Box>
          </motion.div>

          {/* Try It Today Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  mb: 2,
                  color: '#ffffff',
                }}
              >
                Try It Today
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#e2e8f0',
                    fontSize: '0.9rem',
                    lineHeight: 1.4,
                    maxWidth: '280px',
                  }}
                >
                  Get started for free. Enjoy the 7 day free trial
                </Typography>
              </Box>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#ffffff',
                  color: '#041D5C',
                  px: 3,
                  py: 1,
                  borderRadius: '6px',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#f8f9fa',
                  },
                }}
              >
                Get Started
              </Button>
            </Box>
          </motion.div>
        </Box>

        {/* Bottom Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Box
            sx={{
              borderTop: '1px solid rgba(226, 232, 240, 0.2)',
              pt: 4,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 3, sm: 0 },
            }}
          >
            {/* Left Links */}
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                order: { xs: 2, sm: 1 },
              }}
            >
              <Link
                href="#"
                sx={{
                  color: '#e2e8f0',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  '&:hover': {
                    color: '#ffffff',
                    textDecoration: 'none',
                  },
                }}
              >
                Terms & privacy
              </Link>
              <Link
                href="#"
                sx={{
                  color: '#e2e8f0',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  '&:hover': {
                    color: '#ffffff',
                    textDecoration: 'none',
                  },
                }}
              >
                Security
              </Link>
            </Box>

            {/* Social Media Icons */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                order: { xs: 1, sm: 2 },
              }}
            >
              <IconButton
                sx={{
                  color: '#e2e8f0',
                  '&:hover': {
                    color: '#ffffff',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <FacebookIcon fontSize="small" />
              </IconButton>
              <IconButton
                sx={{
                  color: '#e2e8f0',
                  '&:hover': {
                    color: '#ffffff',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <TwitterIcon fontSize="small" />
              </IconButton>
              <IconButton
                sx={{
                  color: '#e2e8f0',
                  '&:hover': {
                    color: '#ffffff',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <LinkedInIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default Footer;
